import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradeLockCard } from '../components/UpgradeLock';
import { useConfirm } from './ConfirmDialog';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  MicrosoftTeamsLogo, SlackLogo, Trash, PaperPlaneRight, Link as LinkIcon,
  CheckCircle, ArrowClockwise,
} from '@phosphor-icons/react';

const EVENT_LABELS = [
  { key: 'created',        label: 'Task created' },
  { key: 'updated',        label: 'Task updated (title, description, priority, due date)' },
  { key: 'status_changed', label: 'Status changed' },
  { key: 'assigned',       label: 'Task assigned' },
  { key: 'commented',      label: 'New comment' },
];

// Provider config table keeps the Slack + Teams cards DRY. Every integration
// type only differs in a few spots: icon, brand color, display names, and
// the channel-picker shape (Teams needs a Team→Channel drill-down, Slack
// just picks a channel directly).
const PROVIDERS = {
  ms_teams: {
    type: 'ms_teams',
    label: 'Microsoft Teams',
    brand: '#5059C9',
    Icon: MicrosoftTeamsLogo,
    iconWeight: 'fill',
    connectRoute: 'ms-teams/connect',
    postMessageSource: 'inspiro-ms-teams',
    usesTeamDrill: true,
    description: 'Post real-time updates into your Teams channels when items change.',
  },
  slack: {
    type: 'slack',
    label: 'Slack',
    brand: '#4A154B',
    Icon: SlackLogo,
    iconWeight: 'fill',
    connectRoute: 'slack/connect',
    postMessageSource: 'inspiro-slack',
    usesTeamDrill: false,
    description: 'Post real-time updates into your Slack channels when items change.',
  },
};

// Unified channel-picker. For MS Teams: Team dropdown -> Channel dropdown.
// For Slack: single Channel dropdown (the workspace is already implied by
// the bot token).
function ChannelPickerDialog({ open, onClose, projectId, integration, provider, onSaved }) {
  const { getOrgHeaders, API } = useAuth();
  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [workspaceLabel, setWorkspaceLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  useEffect(() => {
    if (!open || !projectId || !provider) return;
    setError(''); setTeams([]); setChannels([]); setTeamId(''); setChannelId(''); setWorkspaceLabel('');
    setLoading(true);
    if (provider.usesTeamDrill) {
      axios.get(`${API}/projects/${projectId}/integrations/ms-teams/teams`, h())
        .then(({ data }) => setTeams(data.teams || []))
        .catch(err => handleUpstreamError(err, provider, setError))
        .finally(() => setLoading(false));
    } else {
      axios.get(`${API}/projects/${projectId}/integrations/slack/channels`, h())
        .then(({ data }) => { setChannels(data.channels || []); setWorkspaceLabel(data.team_name || ''); })
        .catch(err => handleUpstreamError(err, provider, setError))
        .finally(() => setLoading(false));
    }
  }, [open, projectId, provider, API, h]);

  const onSelectTeam = (id) => {
    setTeamId(id); setChannelId(''); setChannels([]); setError('');
    if (!id) return;
    setLoadingChannels(true);
    axios.get(`${API}/projects/${projectId}/integrations/ms-teams/teams/${id}/channels`, h())
      .then(({ data }) => setChannels(data.channels || []))
      .catch(err => handleUpstreamError(err, provider, setError))
      .finally(() => setLoadingChannels(false));
  };

  const submit = async () => {
    if (!channelId || !integration) return;
    if (provider.usesTeamDrill && !teamId) return;
    const team = teams.find(t => t.id === teamId);
    const ch = channels.find(c => c.id === channelId);
    setSubmitting(true); setError('');
    try {
      const body = provider.usesTeamDrill
        ? { team_id: teamId, team_name: team?.name || '', channel_id: channelId, channel_name: ch?.name || '' }
        : { channel_id: channelId, channel_name: ch?.name || '' };
      const { data } = await axios.put(`${API}/integrations/${integration.integration_id}/channel`, body, h());
      onSaved(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save channel mapping');
    } finally { setSubmitting(false); }
  };

  if (!provider) return null;
  const { Icon, brand, label } = provider;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid={`${provider.type}-channel-picker`}>
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight flex items-center gap-2">
            <Icon size={20} style={{ color: brand }} weight="fill" /> Pick a {label} channel
          </DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">
            Notifications for this project will be posted to the channel you pick below.
            {workspaceLabel && ` Workspace: ${workspaceLabel}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {provider.usesTeamDrill && (
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold">Team</label>
              <select value={teamId} onChange={(e) => onSelectTeam(e.target.value)} disabled={loading}
                data-testid={`${provider.type}-picker-team`}
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-zinc-200 bg-white text-[13px] focus:outline-none focus:ring-2"
                style={{ boxShadow: `0 0 0 0 ${brand}33` }}>
                <option value="">{loading ? 'Loading your Teams…' : 'Select a team'}</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {(!provider.usesTeamDrill || teamId) && (
            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-zinc-600 font-bold">Channel</label>
              <select value={channelId} onChange={(e) => setChannelId(e.target.value)}
                disabled={loadingChannels || (!provider.usesTeamDrill && loading)}
                data-testid={`${provider.type}-picker-channel`}
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-zinc-200 bg-white text-[13px] focus:outline-none focus:ring-2">
                <option value="">
                  {(loadingChannels || (!provider.usesTeamDrill && loading)) ? 'Loading channels…' : 'Select a channel'}
                </option>
                {channels.map(c => (
                  <option key={c.id} value={c.id}>
                    {provider.usesTeamDrill
                      ? `${c.name}${c.type && c.type !== 'standard' ? ` (${c.type})` : ''}`
                      : `#${c.name}${c.is_private ? ' (private)' : ''}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && <p className="text-[12px] text-red-600" data-testid={`${provider.type}-picker-error`}>{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="h-9 px-3 text-[12px] text-zinc-600">Cancel</Button>
            <Button type="button" disabled={!channelId || (provider.usesTeamDrill && !teamId) || submitting}
              onClick={submit} data-testid={`${provider.type}-picker-save`}
              className="h-9 px-5 text-white text-[13px] font-semibold"
              style={{ backgroundColor: brand }}>
              {submitting ? 'Saving…' : 'Save channel'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function handleUpstreamError(err, provider, setError) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || `Failed to load channels from ${provider.label}.`;
  if (status === 502) {
    setError(`Your ${provider.label} connection has expired. Please close this dialog and click "Reconnect" to sign in again.`);
  } else {
    setError(detail);
  }
}

// Renders a single integration card (Teams or Slack). Shared connect /
// disconnect / reconnect / channel-mapping / event-toggle logic.
function IntegrationCard({ provider, integration, projectId, canManage, onOpenPicker, onChange, toast, setToast }) {
  const { getOrgHeaders, API } = useAuth();
  const confirm = useConfirm();
  const [pendingId, setPendingId] = useState(null);
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const connected = !!integration;
  const mapped = connected && integration.channel_id && (!provider.usesTeamDrill || integration.team_id);
  const busy = pendingId === integration?.integration_id;
  const { Icon, brand, label } = provider;

  const openConnectPopup = () => {
    const url = `${API}/integrations/${provider.connectRoute}?project_id=${encodeURIComponent(projectId)}`;
    const w = 600, hgt = 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - hgt) / 2;
    window.open(url, `inspiro_${provider.type}_oauth`, `width=${w},height=${hgt},left=${left},top=${top}`);
  };

  const notify = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 4000); };

  const toggleEvent = async (eventKey, value) => {
    setPendingId(integration.integration_id);
    try {
      const { data } = await axios.put(`${API}/integrations/${integration.integration_id}`,
        { events: { [eventKey]: value } }, h());
      onChange(data);
    } catch (e) { notify('error', 'Failed to update event'); }
    finally { setPendingId(null); }
  };

  const toggleEnabled = async (value) => {
    setPendingId(integration.integration_id);
    try {
      const { data } = await axios.put(`${API}/integrations/${integration.integration_id}`, { enabled: value }, h());
      onChange(data);
    } catch (e) { notify('error', 'Failed'); }
    finally { setPendingId(null); }
  };

  const sendTest = async () => {
    setPendingId(integration.integration_id);
    try {
      await axios.post(`${API}/integrations/${integration.integration_id}/test`, {}, h());
      notify('success', `Test posted to #${integration.channel_name || 'the selected channel'}`);
    } catch (e) { notify('error', e?.response?.data?.detail || `${label} rejected the post.`); }
    finally { setPendingId(null); }
  };

  const disconnect = async () => {
    if (!(await confirm({ title: `Disconnect ${label}?`, description: "Future events won't be posted to this channel. You can reconnect later.", confirmText: 'Disconnect', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/integrations/${integration.integration_id}`, h());
      onChange(null);
      notify('success', `${label} disconnected.`);
    } catch (e) { notify('error', 'Failed to disconnect'); }
  };

  // Subtitle describing the signed-in account/workspace.
  let connectedBy = '';
  if (connected) {
    if (provider.type === 'ms_teams') connectedBy = integration.connected_user_email || integration.connected_user_name || 'Teams user';
    else if (provider.type === 'slack') connectedBy = integration.slack_team_name ? `Workspace: ${integration.slack_team_name}` : 'Slack workspace';
  }

  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white" data-testid={`${provider.type}-card`}>
      <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-100">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${brand}1A` }}>
          <Icon size={22} style={{ color: brand }} weight="fill" />
        </div>
        <div className="flex-1">
          <h3 className="text-[13px] font-bold text-zinc-900 flex items-center gap-2">
            {label}
            {connected && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                <CheckCircle size={10} weight="fill" /> Connected
              </span>
            )}
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {connected ? `${connectedBy}. Post real-time updates into your channels.` : provider.description}
          </p>
        </div>
        {canManage && !connected && (
          <Button size="sm" onClick={openConnectPopup} data-testid={`${provider.type}-connect-btn`}
            className="h-8 text-white text-[11px]" style={{ backgroundColor: brand }}>
            <Icon size={12} weight="fill" className="mr-1" /> Connect to {label}
          </Button>
        )}
        {canManage && connected && (
          <Button size="sm" variant="outline" onClick={openConnectPopup} data-testid={`${provider.type}-reconnect-btn`}
            className="h-8 text-[11px]">
            <ArrowClockwise size={12} className="mr-1" /> Reconnect
          </Button>
        )}
      </div>

      {toast && toast.providerType === provider.type && (
        <div className={`px-5 py-2 text-[11px] font-medium ${toast.type === 'error' ? 'bg-red-50 text-red-700 border-t border-red-100' : 'bg-emerald-50 text-emerald-700 border-t border-emerald-100'}`} data-testid={`${provider.type}-toast`}>
          {toast.msg}
        </div>
      )}

      {!connected && (
        <div className="px-5 py-8 text-center">
          <p className="text-[12px] text-zinc-500">
            Not connected. Click <span className="font-semibold">Connect to {label}</span> and sign in to start receiving notifications.
          </p>
        </div>
      )}

      {connected && (
        <div className="p-5 space-y-4" data-testid={`${provider.type}-integration-${integration.integration_id}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-400 font-bold">Posting channel</p>
              {mapped ? (
                <p className="text-[14px] font-semibold text-zinc-900 mt-0.5">
                  {integration.team_name && <span className="text-zinc-500 font-normal">{integration.team_name} · </span>}
                  #{integration.channel_name}
                </p>
              ) : (
                <p className="text-[13px] text-amber-700 font-medium mt-0.5">No channel selected yet — pick one to enable notifications.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => onOpenPicker(provider, integration)}
                  data-testid={`${provider.type}-change-channel-btn`} className="h-8 px-3 text-[11px]">
                  {mapped ? 'Change channel' : 'Pick channel'}
                </Button>
              )}
              {canManage && mapped && (
                <Button size="sm" variant="outline" disabled={busy} onClick={sendTest}
                  data-testid={`${provider.type}-test-${integration.integration_id}`} className="h-8 px-3 text-[11px]">
                  <PaperPlaneRight size={11} className="mr-1" /> Send test
                </Button>
              )}
              {canManage && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={disconnect}
                  data-testid={`${provider.type}-disconnect-${integration.integration_id}`}
                  className="h-8 px-2 text-[11px] text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash size={11} />
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
            <Switch checked={integration.enabled} disabled={busy || !canManage}
              onCheckedChange={toggleEnabled}
              data-testid={`${provider.type}-enabled-${integration.integration_id}`} />
            <span className="text-[12px] text-zinc-600">{integration.enabled ? 'Active' : 'Paused'}</span>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold mb-2">Notify on</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {EVENT_LABELS.map(e => (
                <label key={e.key} className="flex items-center gap-2 text-[12px] text-zinc-700 cursor-pointer">
                  <Switch checked={!!integration.events?.[e.key]}
                    disabled={busy || !canManage || !integration.enabled || !mapped}
                    onCheckedChange={(v) => toggleEvent(e.key, v)}
                    data-testid={`${provider.type}-event-${integration.integration_id}-${e.key}`} />
                  <span className={(integration.enabled && mapped) ? '' : 'text-zinc-400'}>{e.label}</span>
                </label>
              ))}
            </div>
          </div>

          {provider.type === 'slack' && (
            <div className="pt-3 border-t border-zinc-100" data-testid="slack-inbound-info">
              <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold mb-2">Two-way actions</p>
              <ul className="text-[12px] text-zinc-700 space-y-1 leading-relaxed">
                <li><code className="font-mono text-[11px] bg-zinc-100 px-1 py-0.5 rounded">/inspiro create &lt;title&gt;</code> — create a task from Slack</li>
                <li><code className="font-mono text-[11px] bg-zinc-100 px-1 py-0.5 rounded">/inspiro assign &lt;KEY&gt; @user</code>, <code className="font-mono text-[11px] bg-zinc-100 px-1 py-0.5 rounded">/inspiro status &lt;KEY&gt; done</code>, <code className="font-mono text-[11px] bg-zinc-100 px-1 py-0.5 rounded">/inspiro my</code></li>
                <li>Right-click any Slack message → <em>More message actions</em> → <strong>Create task from message</strong></li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main section rendered inside ProjectSettingsPage "Integrations".
export default function ProjectIntegrationsSection({ projectId, canManage }) {
  const { getOrgHeaders, API } = useAuth();
  const { hasCap, loading: subLoading } = useSubscription();
  const [integrations, setIntegrations] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerProvider, setPickerProvider] = useState(null);
  const [pickerIntegration, setPickerIntegration] = useState(null);
  const [toast, setToast] = useState(null);

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const integrationsUnlocked = hasCap('integrations_slack') || hasCap('integrations_teams');

  const fetchAll = useCallback(async () => {
    if (!projectId || !integrationsUnlocked) return;
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/integrations`, h());
      setIntegrations(data);
    } catch (e) { console.error(e); }
  }, [API, projectId, h, integrationsUnlocked]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Listen for OAuth popup completion from either provider.
  useEffect(() => {
    const onMessage = async (e) => {
      if (!e?.data) return;
      const src = e.data.source;
      const provider = src === 'inspiro-ms-teams' ? PROVIDERS.ms_teams
                     : src === 'inspiro-slack'    ? PROVIDERS.slack
                     : null;
      if (!provider) return;
      if (e.data.status !== 'connected' || e.data.project_id !== projectId) return;
      const { data } = await axios.get(`${API}/projects/${projectId}/integrations`, h());
      setIntegrations(data);
      const fresh = data.find(x => x.type === provider.type);
      if (fresh && !fresh.channel_id) {
        setPickerProvider(provider);
        setPickerIntegration(fresh);
        setShowPicker(true);
      }
      setToast({ providerType: provider.type, type: 'success', msg: `Connected to ${provider.label}. Pick a channel to finish setup.` });
      setTimeout(() => setToast(null), 4000);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [projectId, API, h]);

  const onOpenPicker = (provider, integration) => {
    setPickerProvider(provider);
    setPickerIntegration(integration);
    setShowPicker(true);
  };

  const onChannelSaved = (updated) => {
    setIntegrations(prev => {
      const exists = prev.find(x => x.integration_id === updated.integration_id);
      return exists
        ? prev.map(x => x.integration_id === updated.integration_id ? updated : x)
        : [updated, ...prev];
    });
    setShowPicker(false);
    setToast({ providerType: updated.type, type: 'success', msg: `Notifications will post to #${updated.channel_name}.` });
    setTimeout(() => setToast(null), 4000);
  };

  const onCardChange = (provider, next) => {
    setIntegrations(prev => {
      const filtered = prev.filter(x => x.type !== provider.type);
      return next ? [next, ...filtered] : filtered;
    });
  };

  return (
    <section data-testid="integrations-section" className="space-y-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <LinkIcon size={18} className="text-zinc-500" />
          <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Integrations</h2>
        </div>
      </div>

      {!subLoading && !integrationsUnlocked ? (
        <UpgradeLockCard
          title="Integrations are a Business plan feature"
          description="Connect Slack and Microsoft Teams to post real-time task updates into your workspace channels. Upgrade to unlock both integrations."
          featureName="integrations_slack + integrations_teams"
          testId="integrations-upgrade-lock"
        />
      ) : (
        <>
          {Object.values(PROVIDERS).map(provider => {
            const integration = integrations.find(x => x.type === provider.type);
            return (
              <IntegrationCard
                key={provider.type}
                provider={provider}
                integration={integration}
                projectId={projectId}
                canManage={canManage}
                onOpenPicker={onOpenPicker}
                onChange={(next) => onCardChange(provider, next)}
                toast={toast}
                setToast={setToast}
              />
            );
          })}

          <ChannelPickerDialog
            open={showPicker}
            onClose={() => setShowPicker(false)}
            projectId={projectId}
            integration={pickerIntegration}
            provider={pickerProvider}
            onSaved={onChannelSaved}
          />
        </>
      )}
    </section>
  );
}
